import { useState, useEffect, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { FormattedTextarea } from '@/components/ui/formatted-textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { Loader2, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CompanyCombobox } from '@/components/ui/company-combobox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const formSchema = z.object({
  company_id: z.string().min(1, 'Company selection is required'),
  position: z.string().min(1, 'Position is required'),
  start_date: z.string().min(1, 'Start date is required'),
  end_date: z.string().nullable(),
  current: z.boolean().default(false),
  description: z.string().optional(),
  location: z.string().optional(),
}).refine((data) => {
  // If not current position, end date is required
  if (!data.current && !data.end_date) {
    return false;
  }
  // If current position, end date should be null
  if (data.current && data.end_date) {
    return false;
  }
  // Start date should be before end date if not current
  if (!data.current && data.end_date && data.start_date > data.end_date) {
    return false;
  }
  return true;
}, {
  message: "Please provide valid dates",
  path: ["end_date"]
});

interface ExperienceFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  experience?: any;
  onSuccess: () => void;
  mode?: 'dialog' | 'inline';
}

export default function ExperienceForm({
  open,
  onOpenChange,
  experience,
  onSuccess,
  mode = 'dialog',
}: ExperienceFormProps) {
  const { id: profileId } = useParams<{ id: string }>();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  
  // Create refs for each input field
  const positionRef = useRef<HTMLInputElement>(null);
  const locationRef = useRef<HTMLInputElement>(null);
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      company_id: '',
      position: '',
      start_date: new Date().toISOString().split('T')[0],
      end_date: null,
      current: false,
      description: '',
      location: '',
    },
  });

  useEffect(() => {
    if (experience) {
      const startDate = experience.start_date ? new Date(experience.start_date).toISOString().split('T')[0] : '';
      const endDate = experience.end_date ? new Date(experience.end_date).toISOString().split('T')[0] : null;
      
      form.reset({
        company_id: experience.company_id || '',
        position: experience.position || '',
        start_date: startDate,
        end_date: endDate,
        current: experience.current || false,
        description: experience.description || '',
        location: experience.location || '',
      });
      setSelectedCompanyId(experience.company_id || '');
    } else {
      form.reset({
        company_id: '',
        position: '',
        start_date: new Date().toISOString().split('T')[0],
        end_date: null,
        current: false,
        description: '',
        location: '',
      });
      setSelectedCompanyId('');
    }
  }, [experience, form, open]);

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    if (!profileId) return;

    setLoading(true);
    try {
      let company_name = '';
      
      if (values.company_id) {
        const { data: companyData, error: companyError } = await supabase
          .from('companies')
          .select('name')
          .eq('id', values.company_id)
          .single();
          
        if (!companyError && companyData) {
          company_name = companyData.name;
        }
      }

      const experienceData = {
        profile_id: profileId,
        company_id: values.company_id,
        company_name: company_name,
        position: values.position,
        start_date: values.start_date,
        end_date: values.current ? null : values.end_date,
        current: values.current,
        description: values.description || '',
        location: values.location || '',
      };

      if (experience) {
        const { error } = await supabase
          .from('experiences')
          .update(experienceData)
          .eq('id', experience.id);

        if (error) {
          if (error.code === '23503') {
            toast({
              variant: 'destructive',
              title: 'Error',
              description: 'The selected company no longer exists. Please select another company.',
            });
            return;
          }
          throw error;
        }
        
        toast({
          title: 'Success',
          description: 'Experience updated successfully',
        });
      } else {
        const { error } = await supabase
          .from('experiences')
          .insert([experienceData]);

        if (error) {
          if (error.code === '23503') {
            toast({
              variant: 'destructive',
              title: 'Error',
              description: 'The selected company no longer exists. Please select another company.',
            });
            return;
          }
          throw error;
        }
        
        toast({
          title: 'Success',
          description: 'Experience added successfully',
        });
      }

      onSuccess();
      if (mode === 'dialog') {
        onOpenChange(false);
      } else {
        form.reset({
          company_id: '',
          position: '',
          start_date: new Date().toISOString().split('T')[0],
          end_date: null,
          current: false,
          description: '',
          location: '',
        });
        setSelectedCompanyId('');
      }
    } catch (error: any) {
      console.error('Error saving experience:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to save experience',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.reset();
    onOpenChange(false);
  };

  // Handle company selection
  const handleCompanyChange = useCallback((value: string) => {
    setSelectedCompanyId(value);
    form.setValue('company_id', value);
  }, [form]);

  const FormContent = () => (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="company_id"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel>Company</FormLabel>
              <FormControl>
                <CompanyCombobox 
                  value={selectedCompanyId}
                  onChange={handleCompanyChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="position"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Position</FormLabel>
              <FormControl>
                <Input 
                  ref={positionRef}
                  placeholder="Job title" 
                  {...field} 
                  onClick={(e) => e.stopPropagation()}
                  onFocus={(e) => e.stopPropagation()}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex items-end gap-4">
          <FormField
            control={form.control}
            name="start_date"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormLabel>Start Date</FormLabel>
                <FormControl>
                  <Input 
                    type="date" 
                    {...field} 
                    onClick={(e) => e.stopPropagation()}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="end_date"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormLabel>End Date</FormLabel>
                <FormControl>
                  <Input 
                    type="date" 
                    {...field} 
                    value={field.value || ''}
                    disabled={form.watch('current')}
                    onClick={(e) => e.stopPropagation()}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="current"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center gap-2">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        field.onChange(checked);
                        if (checked) {
                          form.setValue('end_date', null);
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </FormControl>
                  <FormLabel className="text-sm font-normal">Current Position</FormLabel>
                </div>
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="location"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Location</FormLabel>
              <FormControl>
                <Input 
                  ref={locationRef}
                  placeholder="City, State/Country" 
                  {...field} 
                  onClick={(e) => e.stopPropagation()}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <FormattedTextarea
                  placeholder="Describe your key achievements and responsibilities..."
                  value={field.value || ''}
                  onValueChange={(value) => field.onChange(value)}
                />
              </FormControl>
              <FormDescription>
                Use bullet points to highlight key achievements and responsibilities. Focus on quantifiable results and impact.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end space-x-2 pt-4">
          <Button 
            type="button" 
            variant="outline" 
            onClick={handleCancel}
          >
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {experience ? 'Updating...' : 'Adding...'}
              </>
            ) : (
              experience ? 'Update' : 'Add'
            )}
          </Button>
        </div>
      </form>
    </Form>
  );

  if (mode === 'dialog') {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{experience ? 'Edit' : 'Add'} Experience</DialogTitle>
          </DialogHeader>
          <FormContent />
        </DialogContent>
      </Dialog>
    );
  }

  return open ? (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>{experience ? 'Edit' : 'Add'} Experience</CardTitle>
      </CardHeader>
      <CardContent>
        <FormContent />
      </CardContent>
    </Card>
  ) : null;
}
